import { Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, defer, finalize, Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ACTION_LOADING } from 'src/app/component/store/actions/actions';
import { DataService } from 'src/app/services/data.service';

export type WithLoadingOptions = {
  /**
   * Whether to show a toast when the request fails.
   * Defaults to `true`.
   */
  toastOnError?: boolean;
  /**
   * Optional custom message to show instead of backend message.
   */
  errorMessage?: string;
  /**
   * Optional i18n key for a custom message (wins over `errorMessage`).
   */
  errorMessageKey?: string;
  /**
   * Optional title for the toast.
   */
  errorTitle?: string;
  /**
   * Invoked only after a successful response (before finalize).
   */
  onSuccess?: () => void;
};

export abstract class NewApiDataService extends DataService {
  private readonly _toastrService = this.injector.get(ToastrService);
  private readonly _translateService = this.injector.get(TranslateService);

  protected constructor(
    protected override injector: Injector,
    protected override store: Store<any>,
  ) {
    super(injector, store);
  }

  protected setLoading(isLoading: boolean): void {
    this.isLoading({ action: ACTION_LOADING, payload: isLoading });
  }

  protected withLoading<T>(factory: () => Observable<T>, options?: WithLoadingOptions): Observable<T> {
    return defer(() => {
      this.setLoading(true);
      return factory().pipe(
        tap(() => {
          options?.onSuccess?.();
        }),
        catchError((err) => {
          if (options?.toastOnError !== false) {
            const message =
              options?.errorMessageKey
                ? this._translateService.instant(options.errorMessageKey)
                : options?.errorMessage ?? this._extractBackendErrorMessage(err);
            const safeMessage = (message ?? '').trim() || this._translateService.instant('toastrMessage.error.updateFailed');
            this._toastrService.error(safeMessage, options?.errorTitle ?? '', {
              timeOut: 3000,
              positionClass: 'toast-bottom-right',
            });
          }
          return throwError(() => err);
        }),
        finalize(() => this.setLoading(false)),
      );
    });
  }

  private _extractBackendErrorMessage(err: unknown): string | undefined {
    const e = err as { message?: string; error?: unknown };
    const body = e?.error;

    const fromMessageField = (raw: unknown): string | undefined => {
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
      if (Array.isArray(raw)) {
        const parts = raw.filter((m): m is string => typeof m === 'string').map((s) => s.trim()).filter(Boolean);
        if (parts.length > 0) return parts.join('\n');
      }
      return undefined;
    };

    // Envelope / varias formas del backend (message plano, array de validación, o anidado en `error`).
    if (body !== null && typeof body === 'object') {
      const b = body as Record<string, unknown>;
      const nested = b.error !== null && typeof b.error === 'object' ? (b.error as Record<string, unknown>) : undefined;

      return (
        fromMessageField(b.message) ??
        fromMessageField(nested?.message) ??
        fromMessageField(b.data && typeof b.data === 'object' ? (b.data as Record<string, unknown>).message : undefined) ??
        (typeof b.error === 'string' ? String(b.error).trim() : undefined)
      );
    }

    if (typeof body === 'string' && body.trim()) return body.trim();

    // Último recurso: no usar el mensaje genérico de Angular ("Http failure response for ...").
    const em = typeof e?.message === 'string' ? e.message : '';
    if (em && !em.startsWith('Http failure response')) return em;
    return undefined;
  }
}

